import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Expense, ExpenseStats, CATEGORY_LABELS } from '../types/expense';

export function exportExpensesToPDF(
  expenses: Expense[],
  income: number,
  stats: ExpenseStats,
  year: number = new Date().getFullYear()
) {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('1099 Expense Report', 105, 15, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`Tax Year: ${year}`, 105, 22, { align: 'center' });
  
  // Income and Totals Section
  doc.setFontSize(10);
  doc.text(`Total 1099 Income: $${income.toLocaleString()}`, 20, 35);
  doc.text(`Total Expenses: $${stats.totalExpenses.toLocaleString()}`, 20, 42);
  doc.text(`Expense Ratio: ${((stats.totalExpenses / income) * 100).toFixed(1)}%`, 20, 49);
  doc.text(`Audit Risk Score: ${stats.auditRiskScore}/100`, 120, 35);
  
  // Add risk level badge
  const riskColor = stats.auditRiskScore >= 70 ? '#EF4444' : 
                    stats.auditRiskScore >= 40 ? '#F59E0B' : 
                    stats.auditRiskScore >= 20 ? '#3B82F6' : '#10B981';
  doc.setFillColor(riskColor);
  doc.rect(120, 38, 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  const riskLabel = stats.auditRiskScore >= 70 ? 'HIGH RISK' : 
                    stats.auditRiskScore >= 40 ? 'MEDIUM' : 
                    stats.auditRiskScore >= 20 ? 'LOW-MED' : 'LOW RISK';
  doc.text(riskLabel, 135, 43, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  
  // Expense Details Table
  doc.setFontSize(14);
  doc.text('Expense Details', 20, 60);
  
  const tableData = expenses.map(exp => [
    exp.date,
    CATEGORY_LABELS[exp.category],
    exp.description,
    `$${exp.amount.toFixed(2)}`,
    exp.businessPurpose || 'Not provided'
  ]);
  
  autoTable(doc, {
    startY: 65,
    head: [['Date', 'Category', 'Description', 'Amount', 'Business Purpose']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246] },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 35 },
      2: { cellWidth: 40 },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 60 }
    },
    styles: { fontSize: 8 }
  });
  
  // Schedule C Breakdown (New Page)
  doc.addPage();
  doc.setFontSize(16);
  doc.text('IRS Schedule C - Line Item Breakdown', 105, 20, { align: 'center' });
  doc.setFontSize(10);
  doc.text('(Use these totals when filing your taxes)', 105, 27, { align: 'center' });
  
  // Schedule C mapping
  const scheduleCData = [
    ['8', 'Advertising', stats.expensesByCategory['advertising']],
    ['9', 'Car and truck expenses', stats.expensesByCategory['vehicle']],
    ['10', 'Commissions and fees', stats.expensesByCategory['commissions']],
    ['11', 'Contract labor', stats.expensesByCategory['contract-labor']],
    ['13', 'Depreciation', stats.expensesByCategory['depreciation']],
    ['14', 'Employee benefit programs', 0],
    ['15', 'Insurance (other than health)', stats.expensesByCategory['insurance']],
    ['16a', 'Mortgage interest', 0],
    ['16b', 'Other interest', stats.expensesByCategory['interest']],
    ['17', 'Legal and professional services', stats.expensesByCategory['legal-professional']],
    ['18', 'Office expense', stats.expensesByCategory['office-expense']],
    ['20a', 'Rent or lease (vehicles)', 0],
    ['20b', 'Rent or lease (other)', stats.expensesByCategory['rent-lease']],
    ['21', 'Repairs and maintenance', stats.expensesByCategory['repairs-maintenance']],
    ['22', 'Supplies', stats.expensesByCategory['supplies']],
    ['23', 'Taxes and licenses', stats.expensesByCategory['taxes-licenses']],
    ['24a', 'Travel', stats.expensesByCategory['travel']],
    ['24b', 'Meals (50% deductible)', stats.expensesByCategory['meals'] * 0.5],
    ['25', 'Utilities', stats.expensesByCategory['utilities']],
    ['30', 'Home office deduction', stats.expensesByCategory['home-office']],
    ['27b', 'Other expenses', stats.expensesByCategory['other']]
  ];
  
  const scheduleCFormatted = scheduleCData.map(item => [
    `Line ${item[0]}`,
    item[1],
    `$${(item[2] as number).toFixed(2)}`
  ]);
  
  autoTable(doc, {
    startY: 35,
    head: [['Schedule C Line', 'Description', 'Amount']],
    body: scheduleCFormatted,
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246], fontSize: 10, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: 'bold' },
      1: { cellWidth: 110 },
      2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
    },
    styles: { fontSize: 9 },
    foot: [[
      '',
      'Total Expenses (Line 28)',
      `$${stats.totalExpenses.toFixed(2)}`
    ]],
    footStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' }
  });
  
  // Risk Factors Page
  if (stats.riskFactors.length > 0) {
    doc.addPage();
    doc.setFontSize(16);
    doc.text('Audit Risk Factors & Recommendations', 105, 20, { align: 'center' });
    
    let yPos = 35;
    stats.riskFactors.forEach((factor, idx) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Risk factor box
      const boxColor = factor.severity === 'high' ? '#FEF2F2' : 
                      factor.severity === 'medium' ? '#FFFBEB' : '#EFF6FF';
      const borderColor = factor.severity === 'high' ? '#EF4444' : 
                         factor.severity === 'medium' ? '#F59E0B' : '#3B82F6';
      
      doc.setFillColor(boxColor);
      doc.setDrawColor(borderColor);
      doc.setLineWidth(0.5);
      doc.rect(15, yPos - 5, 180, 25, 'FD');
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${idx + 1}. ${factor.category} - ${factor.severity.toUpperCase()}`, 20, yPos);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const issueLines = doc.splitTextToSize(factor.issue, 170);
      doc.text(issueLines, 20, yPos + 6);
      
      doc.setFont('helvetica', 'italic');
      const recLines = doc.splitTextToSize(`💡 ${factor.recommendation}`, 170);
      doc.text(recLines, 20, yPos + 12);
      
      yPos += 30;
    });
  }
  
  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated by Free1099Calc.com - ${new Date().toLocaleDateString()} - Page ${i} of ${pageCount}`,
      105,
      285,
      { align: 'center' }
    );
    doc.text(
      'This is an educational tool. Consult a CPA before filing.',
      105,
      290,
      { align: 'center' }
    );
  }
  
  // Save the PDF
  doc.save(`1099-Expense-Report-${year}.pdf`);
}
